import {Request} from "../../core/request";
import {AuthHeader} from "../../core/token";
import type {CourseLocation} from "../../course/location";
import type {
    GetClassListRequest,
    GetClassListResponse,
    CreateClassRequest,
    AddPersonToClassRequest,
    UpdatePersonSubjectsRequest,
    DeletePersonFromClassRequest,
    UpdateClassStatusRequest,
    UpdateClassRequest,
    AddClassHomeworkBankRequest,
    AdminClass,
    AdminClassDetail,
    AdminClassUserAss
} from "./types";

// 获取班级列表
export async function getClassList(req: GetClassListRequest): Promise<GetClassListResponse> {
    const resp = await Request.get(`/admin_manager/class/`, {
        headers: AuthHeader(),
        params: req,
    });
    return resp.data;
}

// 获取班级详情
export async function getClassDetail(classID: number): Promise<{data: AdminClassDetail}> {
    const resp = await Request.get(`/admin_manager/class/${classID}`, {
        headers: AuthHeader(),
    });
    return resp.data;
}

// 创建班级
export async function createClass(req: CreateClassRequest): Promise<{data: AdminClass}> {
    const resp = await Request.post(`/admin_manager/class/`, req, {
        headers: AuthHeader(),
    });
    return resp.data;
}

// 拷贝班级
export async function copyClass(classID: number): Promise<{data: AdminClass}> {
    const resp = await Request.post(`/admin_manager/class/${classID}/copy`, {}, {
        headers: AuthHeader(),
    });
    return resp.data;
}

// 添加人员到班级
export async function addPersonToClass(classID: number, req: AddPersonToClassRequest): Promise<{data: AdminClassUserAss}> {
    const resp = await Request.put(`/admin_manager/class/${classID}/add_person`, req, {
        headers: AuthHeader(),
    });
    return resp.data;
}

// 修改老师科目
export async function updatePersonSubjects(classID: number, req: UpdatePersonSubjectsRequest): Promise<{data: AdminClassUserAss}> {
    const resp = await Request.put(`/admin_manager/class/${classID}/update_person`, req, {
        headers: AuthHeader(),
    });
    return resp.data;
}

// 从班级删除人员
export async function deletePersonFromClass(classID: number, req: DeletePersonFromClassRequest): Promise<{data: string}> {
    const resp = await Request.put(`/admin_manager/class/${classID}/delete_person`, req, {
        headers: AuthHeader(),
    });
    return resp.data;
}

// 更新班级信息
export async function updateClass(classID: number, req: UpdateClassRequest): Promise<{data: AdminClass}> {
    const resp = await Request.put(`/admin_manager/class/${classID}`, req, {
        headers: AuthHeader(),
    });
    return resp.data;
}

// 更新班级状态
export async function updateClassStatus(classID: number, req: UpdateClassStatusRequest): Promise<{data: AdminClass}> {
    const resp = await Request.put(`/admin_manager/class/${classID}/status`, req, {
        headers: AuthHeader(),
    });
    return resp.data;
}

// 删除班级
export async function deleteClass(classID: number): Promise<{data: string}> {
    const resp = await Request.delete(`/admin_manager/class/${classID}`, {
        headers: AuthHeader(),
    });
    return resp.data;
}

// 获取班级授权作业列表
export async function getClassHomeworkBank(classID: number): Promise<{data: any[]}> {
    const resp = await Request.get(`/admin_manager/class/${classID}/homework_bank`, {
        headers: AuthHeader(),
    });
    return resp.data;
}

// 添加班级授权作业
export async function addClassHomeworkBank(classID: number, req: AddClassHomeworkBankRequest): Promise<{data: any}> {
    const resp = await Request.post(`/admin_manager/class/${classID}/homework_bank`, req, {
        headers: AuthHeader(),
    });
    return resp.data;
}

// 删除班级授权作业
export async function deleteClassHomeworkBank(classID: number, homeworkBankID: number): Promise<{data: string}> {
    const resp = await Request.delete(`/admin_manager/class/${classID}/homework_bank/${homeworkBankID}`, {
        headers: AuthHeader(),
    });
    return resp.data;
}

// 管理员：获取班级可下发的作业库（仅授权+全局）
export async function getAdminClassHomeworkBanksForAssign(classID: number, subjectID: number): Promise<{data: any[]}> {
    const resp = await Request.get(`/admin_manager/class/${classID}/homework_banks_for_assign`, {
        headers: AuthHeader(),
        params: { subjectID },
    });
    return resp.data;
}

// 管理员：获取班级作业列表
export async function getAdminClassHomeworks(classID: number, params?: { page?: number; size?: number; subject?: string; keyword?: string }): Promise<{data: any[]; total: number; page: number; size: number}> {
    const resp = await Request.get(`/admin_manager/class/${classID}/homeworks`, {
        headers: AuthHeader(),
        params,
    });
    return resp.data;
}

// 管理员：创建作业
export async function createAdminHomework(classID: number, req: { temp_id: number; name?: string; description?: string; enable?: boolean; manner_read?: boolean; exam_duration_minutes?: number }): Promise<{data: any}> {
    const resp = await Request.post(`/admin_manager/class/${classID}/homework`, req, {
        headers: AuthHeader(),
    });
    return resp.data;
}

// 管理员：获取作业详情
export async function getAdminHomeworkDetails(classID: number, homeworkID: number): Promise<{data: any}> {
    const resp = await Request.get(`/admin_manager/class/${classID}/homework/${homeworkID}/details`, {
        headers: AuthHeader(),
    });
    return resp.data;
}

// 管理员：获取学生答案详情
export async function getAdminStudentAnswerDetail(classID: number, homeworkID: number, userId: number): Promise<{data: any}> {
    const resp = await Request.get(`/admin_manager/class/${classID}/homework/${homeworkID}/student/${userId}/answer`, {
        headers: AuthHeader(),
    });
    return resp.data;
}

// 管理员：批改作业
export async function gradeAdminHomework(classID: number, homeworkID: number, answerId: number, req: { question_scores: Array<{ question_id: string; correctness: string; score: number; desc?: string }> }): Promise<{data: any}> {
    const resp = await Request.post(`/admin_manager/class/${classID}/homework/${homeworkID}/grade/${answerId}`, req, {
        headers: AuthHeader(),
    });
    return resp.data;
}

// 管理员：获取作业库模板列表
export async function getAdminHomeworkBankTemps(bankId: number, params?: { page?: number; size?: number; keyword?: string }): Promise<{data: any[]; total: number}> {
    const resp = await Request.get(`/admin_manager/homework_bank/${bankId}/temps`, {
        headers: AuthHeader(),
        params: { total: 0, page: params?.page || 1, size: params?.size || 10, keyword: params?.keyword || '' },
    });
    return resp.data;
}

// 管理员：获取作业库模板详情
export async function getAdminHomeworkBankTemp(bankId: number, tempId: number): Promise<any> {
    const resp = await Request.get(`/admin_manager/homework_bank/${bankId}/temps/${tempId}`, {
        headers: AuthHeader(),
    });
    return resp.data;
}

// 管理员：获取科目列表
export async function getAdminSubjects(): Promise<any[]> {
    const resp = await Request.get(`/admin_manager/subject`, {
        headers: AuthHeader(),
    });
    return resp.data;
}

// 管理员：获取校区列表
export async function getAdminCourseLocations(): Promise<CourseLocation[]> {
    const resp = await Request.get(`/admin_manager/course_location`, {
        headers: AuthHeader(),
    });
    return resp.data;
}

// 管理员：获取教师列表
export async function getAdminUsers() {
    return await Request.get("/admin_manager/users", {headers: AuthHeader()});
}
